// apps/web/src/features/inventory/components/VenueInventoryDisplay.tsx
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import { useVenueInventory } from '../hooks/useVenueInventory';
import { getCategoryIcon } from '../../bookings/components/floor-view/categoryIcons';
import type { VenueInventoryResponse, InventoryCategoryValue } from '@lov/shared';

function getCategoryInfo(category: InventoryCategoryValue) {
  return getCategoryIcon(category);
}

// Styled Components
const ItemCard = styled(Card)(({ theme }) => ({
  padding: 16,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: alpha(theme.palette.success.main, 0.3),
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },
}));

const ItemName = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const QuantityText = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
}));

const PriceText = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.success.main,
  marginTop: 4,
}));

const CountChip = styled(Chip)(({ theme }) => ({
  marginLeft: 8,
  height: 22,
  backgroundColor: alpha(theme.palette.success.main, 0.08),
  color: theme.palette.success.main,
  '& .MuiChip-label': {
    padding: '0 8px',
    fontSize: '0.6875rem',
    fontFamily: '"IBM Plex Mono", monospace',
  },
}));

const MoreText = styled(Typography)(({ theme }) => ({
  marginTop: 16,
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  textAlign: 'center',
}));

const EmptyText = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  padding: '24px 0',
}));

interface InventoryItemCardProps {
  item: VenueInventoryResponse;
}

function InventoryItemCard({ item }: InventoryItemCardProps) {
  const catInfo = getCategoryInfo(item.inventoryItem.category);

  const formatPrice = (price?: number) => {
    if (!price) return null;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <ItemCard>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Avatar
          variant="rounded"
          sx={{
            width: 52,
            height: 52,
            bgcolor: alpha(catInfo.color, 0.1),
            color: catInfo.color,
            borderRadius: '10px',
          }}
        >
          {catInfo.icon}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ItemName>{item.inventoryItem.name}</ItemName>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Chip
              label={catInfo.label}
              size="small"
              sx={{
                height: 20,
                bgcolor: alpha(catInfo.color, 0.1),
                color: catInfo.color,
                '& .MuiChip-label': {
                  px: 1,
                  fontSize: '0.625rem',
                  fontFamily: '"IBM Plex Sans", sans-serif',
                },
              }}
            />
            <QuantityText>x{item.quantity}</QuantityText>
          </Box>
          {item.pricePerHour && <PriceText>{formatPrice(item.pricePerHour)}/hr</PriceText>}
        </Box>
      </Box>
    </ItemCard>
  );
}

function InventoryItemSkeleton() {
  return (
    <ItemCard>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: '10px' }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="80%" height={20} />
          <Skeleton width="40%" height={16} sx={{ mt: 1 }} />
        </Box>
      </Box>
    </ItemCard>
  );
}

interface VenueInventoryDisplayProps {
  venueId: string;
  title?: string;
  maxItems?: number;
}

export function VenueInventoryDisplay({ venueId, maxItems }: VenueInventoryDisplayProps) {
  const { data: inventory, isLoading, error } = useVenueInventory(venueId, false);

  // Filter only available items
  const availableItems = inventory?.filter((item) => item.isAvailable) || [];
  const displayItems = maxItems ? availableItems.slice(0, maxItems) : availableItems;
  const hasMore = maxItems && availableItems.length > maxItems;

  if (error) {
    return null; // Silently fail - don't show error on public venue page
  }

  if (!isLoading && availableItems.length === 0) {
    return <EmptyText>No equipment listed for this venue.</EmptyText>;
  }

  return (
    <Box>
      {!isLoading && availableItems.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CountChip
            label={`${availableItems.length} ${availableItems.length === 1 ? 'item' : 'items'} available`}
            size="small"
          />
        </Box>
      )}

      <Grid container spacing={2}>
        {isLoading
          ? // Show skeleton loaders
            [1, 2, 3, 4].map((i) => (
              <Grid size={{ xs: 12, sm: 6 }} key={i}>
                <InventoryItemSkeleton />
              </Grid>
            ))
          : // Show actual items
            displayItems.map((item) => (
              <Grid size={{ xs: 12, sm: 6 }} key={item.id}>
                <InventoryItemCard item={item} />
              </Grid>
            ))}
      </Grid>

      {hasMore && <MoreText>+{availableItems.length - maxItems} more items available</MoreText>}
    </Box>
  );
}
